import './index.scss';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { IProps, MarkLocation, MarkType, IMarketItem, IMarketOption, MarkAnchor } from './interface';
import { defaultColor, defaultMarkTextColor, defaultFontSize, defaultStrokeWidth } from './dataFlow';

/**
 * 标记文本
 */

const getMarkText = (marker: IMarketItem, option: IMarketOption) => {
    if (!marker?.label) {
        return null;
    }
    const {
        fontSize = defaultFontSize,
        textColor,
        isTextColorFollowColor = false,
        color,
        label,
        textAnchor = MarkAnchor.START,
    } = marker;
    const { progress, ...rest } = option;
    const fill = textColor || (isTextColorFollowColor ? color : defaultMarkTextColor);
    return (
        <text fontSize={fontSize} textAnchor={textAnchor} fill={fill} {...rest}>
            {label}
        </text>
    );
};
/**
 * @desc 标记图形-三角
 */
const getMarkPolygonShape = (marker: IMarketItem, option: React.SVGProps<SVGPolygonElement>) => {
    const { color } = marker;
    return <path {...option} fill={color} />;
};
/**
 * @desc 标记图形-线
 */
const getMarkLineShape = (marker: IMarketItem, option: React.SVGProps<SVGRectElement>) => {
    const { color, strokeWidth = defaultStrokeWidth } = marker;
    return <rect {...option} width={strokeWidth} fill={color} />;
};
const ProgressBarWithMarkers = (props: IProps) => {
    const {
        width = 100,
        offsetWidth = 0,
        className = '',
        color = defaultColor,
        backgroundColor = '#D9D9D9',
        height = 18,
        offsetHeight: defaultOffsetHeight = 0,
        progress = 0,
        markers = [],
        suffixText = '',
        suffixTextConfig = {
            fontSize: defaultFontSize,
            x: width + 5,
        },
    } = props;
    // 计算进度条长度
    const progressLength = (width * progress) / 100;
    const textRef = useRef<SVGTextElement>(null);
    const [svgWidth, setSvgWidth] = useState(width + offsetWidth);
    const [topMarkers, bottomMarkers, offsetHeight] = useMemo(() => {
        const top = markers.filter((marker) => marker?.location === MarkLocation.TOP);
        const bottom = markers.filter((marker) => marker?.location !== MarkLocation.TOP);
        const offsetHeight = (top.length > 0 ? 20 : 0) + (bottom.length > 0 ? 20 : 0) + defaultOffsetHeight;
        return [top, bottom, offsetHeight];
    }, [markers, defaultOffsetHeight]);
    const svgHeight = height + offsetHeight;
    const offsetH = topMarkers.length > 0 ? 26 : 0;
    useEffect(() => {
        if (textRef.current) {
            const bbox = textRef.current.getBBox();
            setSvgWidth(bbox.width + width + offsetWidth);
        }
    }, [suffixText]);

    /**
     * @desc 获取上方标记
     */
    const getMarketTop = (marker: IMarketItem, x: number, hasBottomMarker: boolean) => {
        const isLine = marker.type === MarkType.LINE;
        return isLine
            ? getMarkLineShape(marker, {
                  x,
                  y: 22,
                  height: 21,
              })
            : getMarkPolygonShape(
                  marker,
                  !hasBottomMarker
                      ? {
                            d: `M${x} 24L${x - 4.5} 19.25H${x + 4.5}L${x} 24Z`,
                        }
                      : {
                            d: `M${x} 20L${x - 4.5} 13.25H${x + 4.5}L${x} 20Z`,
                        },
              );
    };
    /**
     * @desc 获取底部标记
     */
    const getMarketBottom = (marker: IMarketItem, x: number, hasTopMarker: boolean) => {
        const isLine = marker.type === MarkType.LINE;
        return isLine
            ? getMarkLineShape(marker, {
                  x,
                  y: 22,
                  height: 21,
              })
            : getMarkPolygonShape(
                  marker,
                  !hasTopMarker
                      ? {
                            d: `M${x} 10L${x - 4.5} 16.75H${x + 4.5}L${x} 10Z`,
                        }
                      : {
                            d: `M${x} 39L${x - 4.5} 45.75H${x + 4.5}L${x} 39Z`,
                        },
              );
    };

    return (
        <div className={`progress-market-bar ${className}`}>
            <svg width={svgWidth} height={svgHeight}>
                {/* 上方标记*/}
                {topMarkers.map((marker, index) => {
                    const markerX = (width * marker.position) / 100;
                    return (
                        <React.Fragment key={index}>
                            {/* 标记类型 */}
                            {getMarketTop(marker, markerX, bottomMarkers.length > 0)}
                            {/* 标记文本 */}
                            {getMarkText(marker, { x: markerX - 4.5, y: (marker?.fontSize || 12) - 2, progress })}
                        </React.Fragment>
                    );
                })}
                {/* 进度条底部背景 */}
                <rect x="0" y={offsetH} width={width} height={height / 2} fill={backgroundColor} />
                {/* 进度条前景 */}
                <rect x="0" y={offsetH} width={progressLength} height={height / 2} fill={color} />
                {/* 下方标记 */}
                {bottomMarkers.map((marker, index) => {
                    const markerX = (width * marker.position) / 100;
                    return (
                        <Fragment key={index}>
                            {/* 标记类型 */}
                            {getMarketBottom(marker, markerX, topMarkers.length > 0)}
                            {/* 标记文本 */}
                            {getMarkText(marker, { x: markerX - 4.5, y: height + offsetH + 14, progress })}
                        </Fragment>
                    );
                })}
                {/* 进度条后缀文本 */}
                <text
                    ref={textRef}
                    y={offsetH + height / 2}
                    textAnchor={MarkAnchor.START}
                    fill={defaultMarkTextColor}
                    {...suffixTextConfig}
                >
                    {suffixText}
                </text>
            </svg>
        </div>
    );
};
export { ProgressBarWithMarkers };
